// Deadbolts (jobq-api "AFTER-HOURS SECURITY: DEADBOLTS", since 2026-10-30). The ONE copy of the calls and wording for
// the Locks part of the Doors screen. JobQ locks a door when the shop arms; it unlocks only for a typed Admin PIN, so
// the Lock / Unlock press sends a PIN typed for that press, never the session's alone.

import { securityFetch } from '../../utils/securityApi';
import { shopClock } from '../../utils/shopDate';

export const LOCKS_POLL_MS = 30000;
export const LOCK_FOLLOW_MS = 2000;      // while a press is under way
export const LOCK_FOLLOW_MAX_MS = 45000; // the API's 30 s to collect it + the lock's own time

export const fetchLocks = () => securityFetch('/security/locks');

export const sendLockCommand = (lockId, action, pin, force = false) =>
  securityFetch(`/security/locks/${lockId}/command`, { method: 'POST', body: JSON.stringify({ pin, action, force }) });

export const setAutoLock = (lockId, autoLock) =>
  securityFetch(`/security/locks/${lockId}`, { method: 'PATCH', body: JSON.stringify({ auto_lock: autoLock }) });

/**
 * What the lock card's badge says. A failed read, a silent Pi, a jam or a lock gone quiet outranks the last bolt report:
 * that report may be stale. `readFailed` = JobQ's last answer to this screen was an error, so all of this is old news.
 */
export function lockStateView(lock, readFailed = false) {
  if (readFailed) return { label: "CAN'T READ", tone: 'bad', note: `JobQ isn't answering this screen. Last known: ${lock.bolt || 'unknown'}.` };
  if (!lock.online) return { label: 'NOT REPORTING', tone: 'bad', note: "The lock service on the laser Pi isn't working. This is the last known state." };
  if (lock.jammed_at) return { label: 'JAMMED', tone: 'bad', note: 'It may not be locked. Check the door lines up with the strike.' };
  if (lock.reachable === false) return { label: 'NO SIGNAL', tone: 'bad', note: "The stick can't reach this lock." };
  if (lock.report_stale) return { label: 'NOT REPORTING', tone: 'bad', note: "This lock hasn't reported in over 3 minutes. This is the last known state." };
  if (lock.bolt === 'locked') return { label: 'LOCKED', tone: 'ok' };
  if (lock.bolt === 'unlocked') return { label: 'UNLOCKED', tone: 'warn' };
  return { label: 'UNKNOWN', tone: 'muted', note: "The lock hasn't said where its bolt is." };
}

/** One line of health under the card's title. */
export function lockHealthLine(lock) {
  const parts = [];
  if (lock.battery !== null && lock.battery !== undefined) parts.push(`Battery ${lock.battery}%${lock.battery_low ? ' (low)' : ''}`);
  if (lock.rssi !== null && lock.rssi !== undefined) parts.push(`Signal ${lock.rssi} dBm`);
  if (lock.door_name) parts.push(`${lock.door_name}: ${lock.door_state || 'state unknown'}`);
  else parts.push(lock.self_closing ? 'Self-closing door, no sensor' : 'No door sensor linked');
  return parts.join(' · ');
}

export const autoLockNote = (lock) => {
  if (!lock.auto_lock) return 'Auto-lock is off: JobQ will not lock this door by itself.';
  if (lock.auto_state === 'failed') return 'Auto-lock could not lock this door for the current arming. People were told.';
  if (lock.auto_state === 'waiting' && lock.self_closing && !lock.door_name) return 'The shop is armed: JobQ is about to lock this door.';
  if (lock.auto_state === 'waiting') return 'The shop is armed: JobQ locks this door once its sensor has read shut for 30 s.';
  if (lock.auto_state === 'sent') return 'The shop is armed: lock command sent.';
  if (lock.auto_state === 'done') return "This arming's lock is finished. An unlock now stays unlocked until the next arming.";
  if (lock.self_closing && !lock.door_name) return 'JobQ locks this door when the shop arms, taking it as shut because it has a closer. It never unlocks by itself.';
  return 'JobQ locks this door when the shop arms, once its sensor reads shut. It never unlocks by itself.';
};

/** Where a press stands, once the command has an outcome. null while it is under way. */
export function commandOutcomeText(command, lockName) {
  if (!command || !command.outcome) return null;
  const past = command.action === 'lock' ? 'locked' : 'unlocked';
  if (command.outcome === 'done') return { kind: 'success', text: `${lockName} is ${past}.` };
  if (command.outcome === 'expired') return { kind: 'danger', text: `Not carried out: the lock service didn't collect it in time. Nothing moved.` };
  return { kind: 'danger', text: `The ${command.action} did not finish: ${command.error || 'no answer from the lock'}. Check the door.` };
}

/** The lock's recent history, newest first: presses, auto-locks and what the lock itself said. */
export function lockActivity(data, lockId, limit = 8) {
  const commands = ((data && data.commands) || []).filter((c) => c.lock_id === lockId).map((c) => ({
    key: `c${c.command_id}`,
    at: c.requested_at,
    text: `${c.source === 'auto' ? 'JobQ auto-lock' : `${c.by || 'Someone'} pressed ${c.action === 'lock' ? 'Lock' : 'Unlock'}`}${c.outcome && c.outcome !== 'done' ? ` (${c.outcome})` : ''}`,
    tone: c.outcome === 'failed' || c.outcome === 'expired' ? 'bad' : 'muted',
  }));
  const events = ((data && data.events) || []).filter((e) => e.lock_id === lockId).map((e) => ({
    key: `e${e.event_id}`,
    at: e.at,
    text: `${e.label || 'Lock report'}${e.code_slot ? ` (code ${e.code_slot})` : ''}`,
    tone: e.kind === 'jam' ? 'bad' : 'muted',
  }));
  return [...commands, ...events]
    .sort((a, b) => String(b.at).localeCompare(String(a.at)))
    .slice(0, limit)
    .map((x) => ({ ...x, when: x.at ? shopClock(x.at) : '' }));
}
